import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { listFulfillmentOrders } from "../repo";
import {
  buildFulfillmentOrderListQueryKey,
  normalizeFulfillmentOrderListFilters,
} from "../service";
import { readFulfillmentOrderListState, writeFulfillmentOrderListState, type FulfillmentOrderListState } from "../service/fulfillmentOrderListService";
import type {
  FulfillmentOrderListFilters,
  FulfillmentOrderSortDirection,
  FulfillmentOrderSortField,
} from "../types";

/** 确认件服务端分页列表运行时。 */
export function useFulfillmentOrderListRuntime() {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => readFulfillmentOrderListState(searchParams), [searchParams]);
  const { page, pageSize, submittedFilters, sortField, sortDirection, sortCleared } = state;
  const [draftFilters, setDraftFilters] = useState<FulfillmentOrderListFilters>(submittedFilters);
  useEffect(() => setDraftFilters(submittedFilters), [submittedFilters]);
  const updateQuery = (patch: Partial<FulfillmentOrderListState>) => {
    setSearchParams(writeFulfillmentOrderListState(searchParams, { ...state, ...patch }));
  };
  const queryKey = useMemo(
    () => buildFulfillmentOrderListQueryKey(page, pageSize, submittedFilters, sortField, sortDirection),
    [page, pageSize, sortDirection, sortField, submittedFilters],
  );
  const query = useQuery({
    queryKey,
    queryFn: () =>
      listFulfillmentOrders({
        page,
        pageSize,
        ...submittedFilters,
        sortField,
        sortDirection,
      }),
    placeholderData: keepPreviousData,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    draftFilters,
    setDraftFilters,
    submittedFilters,
    page,
    pageSize,
    sortField,
    sortDirection,
    sortCleared,
    data: query.data,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    search: () => {
      updateQuery({ submittedFilters: normalizeFulfillmentOrderListFilters(draftFilters), page: 1 });
    },
    reset: () => {
      setDraftFilters({});
      updateQuery({ submittedFilters: {}, page: 1 });
    },
    setPage: (value: number) => updateQuery({ page: value }),
    setPageSize: (value: number) => {
      updateQuery({ pageSize: value, page: 1 });
    },
    setSort: (field: FulfillmentOrderSortField, direction: FulfillmentOrderSortDirection | null) => {
      updateQuery(direction
        ? { sortField: field, sortDirection: direction, sortCleared: false, page: 1 }
        : { sortField: "updated_at", sortDirection: "desc", sortCleared: true, page: 1 });
    },
  };
}
